import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowUpRight, TrendingUp, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { SpotConfidence } from "@/lib/spotRateEngine";

export interface RepricingLoad {
  id: string;
  loadNumber: string;
  originCity: string;
  originState: string;
  destCity: string;
  destState: string;
  currentRate: number;
  currentRpm: number;
  suggestedRate: number;
  suggestedRpm: number;
  totalMiles: number;
  delta: number;
  confidence: SpotConfidence;
  compCount: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loads: RepricingLoad[];
  onApplied: () => void;
}

const confidenceColor: Record<SpotConfidence, string> = {
  high: "hsl(var(--brand-teal-deep))",
  medium: "hsl(35 85% 50%)",
  low: "hsl(0 70% 55%)",
};

export default function RepricingReviewDialog({ open, onOpenChange, loads, onApplied }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set(loads.map(l => l.id)));
  const [applying, setApplying] = useState(false);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === loads.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(loads.map(l => l.id)));
    }
  };

  const selectedLoads = loads.filter(l => selected.has(l.id));
  const totalUplift = selectedLoads.reduce((s, l) => s + l.delta, 0);

  const applyRepricing = async () => {
    if (!selectedLoads.length) return;
    setApplying(true);
    try {
      const results = await Promise.allSettled(
        selectedLoads.map(l =>
          supabase
            .from("loads")
            .update({ rate_amount: l.suggestedRate } as any)
            .eq("id", l.id)
        )
      );
      const succeeded = results.filter(r => r.status === "fulfilled").length;
      toast({
        title: `${succeeded} load${succeeded !== 1 ? "s" : ""} repriced`,
        description: `+$${Math.round(totalUplift).toLocaleString()} estimated uplift applied`,
      });
      onApplied();
      onOpenChange(false);
    } catch (err) {
      console.error("Repricing error:", err);
      toast({ title: "Repricing failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <TrendingUp className="h-4 w-4" style={{ color: "hsl(var(--brand-teal-deep))" }} />
            Review Underpriced Loads
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {/* Header row */}
          <div className="flex items-center gap-3 py-2 border-b border-border text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
            <div className="w-5">
              <Checkbox
                checked={selected.size === loads.length}
                onCheckedChange={toggleAll}
              />
            </div>
            <div className="flex-1">Lane</div>
            <div className="w-20 text-right">Current</div>
            <div className="w-20 text-right">Suggested</div>
            <div className="w-16 text-right">Delta</div>
            <div className="w-16 text-center">Conf.</div>
          </div>

          {loads.map(load => (
            <div
              key={load.id}
              className={`flex items-center gap-3 py-2.5 border-b border-border/50 transition-colors ${
                selected.has(load.id) ? "bg-accent/30" : ""
              }`}
            >
              <div className="w-5">
                <Checkbox
                  checked={selected.has(load.id)}
                  onCheckedChange={() => toggle(load.id)}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">
                  {load.originCity}, {load.originState} → {load.destCity}, {load.destState}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  #{load.loadNumber} · {load.totalMiles.toLocaleString()} mi
                </p>
              </div>
              <div className="w-20 text-right">
                <p className="text-xs font-medium text-foreground">${load.currentRate.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">${load.currentRpm.toFixed(2)}/mi</p>
              </div>
              <div className="w-20 text-right">
                <p className="text-xs font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                  ${load.suggestedRate.toLocaleString()}
                </p>
                <p className="text-[10px] text-muted-foreground">${load.suggestedRpm.toFixed(2)}/mi</p>
              </div>
              <div className="w-16 text-right">
                <span className="text-xs font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                  +${Math.round(load.delta).toLocaleString()}
                </span>
              </div>
              <div className="w-16 flex justify-center">
                <Badge
                  variant="outline"
                  className="text-[9px] px-1.5 py-0 capitalize"
                  style={{ borderColor: confidenceColor[load.confidence], color: confidenceColor[load.confidence] }}
                >
                  <Shield className="h-2.5 w-2.5 mr-0.5" />
                  {load.confidence}
                </Badge>
              </div>
            </div>
          ))}

          {loads.length > 0 && (
            <div className="text-[10px] text-muted-foreground py-2">
              Rates based on {loads[0].compCount}+ live FTL comps within 150–250 mi deadhead radius. IQR-filtered.
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between gap-4 pt-3 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {selectedLoads.length} selected · <span className="font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>+${Math.round(totalUplift).toLocaleString()}</span> uplift
          </div>
          <Button
            onClick={applyRepricing}
            disabled={applying || !selectedLoads.length}
            className="gap-1.5"
            size="sm"
          >
            {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
            Apply {selectedLoads.length} Reprice{selectedLoads.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
